import {
  BusinessOnboardingStepKey,
  IBusinessOnboardStep,
  IBusinessOnboardingStepChild
} from '@query'

export type CompanionStepStatus = 'todo' | 'done' | 'skipped' | 'in_progress'

const isChildCompleted = (child: IBusinessOnboardingStepChild) => child.completed_at !== null
const isChildDismissed = (child: IBusinessOnboardingStepChild) => child.dismissed_at !== null
const isChildOpen = (child: IBusinessOnboardingStepChild) =>
  !isChildCompleted(child) && !isChildDismissed(child)

export const isGroupHandled = (step: IBusinessOnboardStep): boolean =>
  (step.child_steps || []).every(child => !isChildOpen(child))

export const getGroupStatus = (step: IBusinessOnboardStep): CompanionStepStatus => {
  const children = step.child_steps || []
  if (children.length === 0) return 'todo'
  if (children.every(isChildCompleted)) return 'done'
  if (children.every(child => !isChildOpen(child))) return 'skipped'
  if (children.some(isChildCompleted)) return 'in_progress'
  return 'todo'
}

export const getActiveStep = (steps: IBusinessOnboardStep[]): IBusinessOnboardStep | undefined =>
  steps.find(step => (step.child_steps || []).some(isChildOpen))

export const getHandledCount = (steps: IBusinessOnboardStep[]): number =>
  steps.filter(isGroupHandled).length

export const getChildProgress = (step: IBusinessOnboardStep) => {
  const children = step.child_steps || []
  return {
    completed: children.filter(isChildCompleted).length,
    total: children.length
  }
}

const AUTOMATION_GROUP_KEYS = [
  BusinessOnboardingStepKey.BANK_INTEGRATION,
  BusinessOnboardingStepKey.FILES_AND_INVOICES
] as const

const NEW_FLOW_GROUP_KEYS = [
  BusinessOnboardingStepKey.HEAR_ABOUT_US,
  BusinessOnboardingStepKey.ACCRUAL_ACCOUNTING,
  BusinessOnboardingStepKey.LUCA_MESSAGE
] as const

export const isLegacyOnboard = (steps: IBusinessOnboardStep[]): boolean =>
  !steps.some(step => (NEW_FLOW_GROUP_KEYS as readonly string[]).includes(step.group_key))

const BACKFILLED_STEP_GAP_MS = 24 * 60 * 60 * 1000

export const isBackfilledOnboard = (
  businessCreatedAt: string | undefined,
  steps: IBusinessOnboardStep[]
): boolean => {
  const businessCreated = businessCreatedAt ? Date.parse(businessCreatedAt) : NaN
  if (Number.isNaN(businessCreated)) return false

  const stepCreatedAt = steps
    .flatMap(step => step.child_steps || [])
    .reduce<number | null>((earliest, child) => {
      const created = Date.parse(child.created_at)
      if (Number.isNaN(created)) return earliest
      return earliest === null || created < earliest ? created : earliest
    }, null)

  return stepCreatedAt !== null && stepCreatedAt - businessCreated > BACKFILLED_STEP_GAP_MS
}

export const didCompleteAutomationSteps = (steps: IBusinessOnboardStep[]): boolean =>
  AUTOMATION_GROUP_KEYS.every(groupKey => {
    const step = steps.find(item => item.group_key === groupKey)
    return step !== undefined && getGroupStatus(step) === 'done'
  })
